import React, { useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const COLORS = {
  panel: '#1e293b',
  panelAlt: '#273449',
  border: '#334155',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  accent: '#4ecca3',
  danger: '#7f1d1d',
  dangerText: '#fecaca',
};

const COMPONENT_LABELS = {
  active_energy: 'Active energy',
  transmission_network: 'Transmission network',
  generation_capacity: 'Generation capacity',
  legacy: 'Legacy charge',
  network_capacity: 'Network capacity',
  network_demand: 'Network demand',
  lv_subsidy: 'LV subsidy',
  ancillary: 'Ancillary',
  service: 'Service',
  admin: 'Admin',
  reactive_energy: 'Reactive energy',
  electrification_subsidy: 'Electrification subsidy',
  affordability_subsidy: 'Affordability subsidy',
};

const ALL = '__all__';

const uniq = (arr) => Array.from(new Set(arr));

const TariffChargesTable = ({ tariffId, charges, onSaved }) => {
  const components = useMemo(() => uniq(charges.map((c) => c.component)).sort(), [charges]);

  const [component, setComponent] = useState(() =>
    components.includes('active_energy') ? 'active_energy' : components[0] || ''
  );
  const [txZone, setTxZone] = useState(ALL);
  const [voltage, setVoltage] = useState(ALL);
  const [season, setSeason] = useState(ALL);
  const [touPeriod, setTouPeriod] = useState(ALL);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Filter by component first
  const chargesForComponent = useMemo(
    () => charges.filter((c) => c.component === component),
    [charges, component]
  );

  // Available filter values for the selected component
  const available = useMemo(() => ({
    txZone: uniq(chargesForComponent.map((c) => c.tx_zone)).sort(),
    voltage: uniq(chargesForComponent.map((c) => c.voltage_category)).sort(),
    season: uniq(chargesForComponent.map((c) => c.season)).sort(),
    touPeriod: uniq(chargesForComponent.map((c) => c.tou_period)).sort(),
  }), [chargesForComponent]);

  // Apply filters
  const filtered = useMemo(() => {
    return chargesForComponent.filter((c) => {
      if (txZone !== ALL && c.tx_zone !== txZone) return false;
      if (voltage !== ALL && c.voltage_category !== voltage) return false;
      if (season !== ALL && c.season !== season) return false;
      if (touPeriod !== ALL && c.tou_period !== touPeriod) return false;
      return true;
    });
  }, [chargesForComponent, txZone, voltage, season, touPeriod]);

  const beginEdit = (charge) => {
    setEditingId(charge.id);
    setEditValue(String(charge.value));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const commitEdit = (chargeId) => {
    const parsed = parseFloat(editValue);
    if (Number.isNaN(parsed)) {
      setError('Value must be a number');
      return;
    }
    setSaving(true);
    setError(null);
    fetch(`${API}/api/grid-tariffs/${tariffId}/charges/${chargeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: parsed }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(() => {
        setEditingId(null);
        setEditValue('');
        if (onSaved) onSaved();
      })
      .catch((e) => setError('Save failed: ' + e.message))
      .finally(() => setSaving(false));
  };

  const selectStyle = {
    background: COLORS.panel,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '12px',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: COLORS.textDim,
    marginBottom: '4px',
  };

  const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.textDim,
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const tdStyle = {
    padding: '8px 10px',
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    fontSize: '12px',
  };

  const renderDim = (v) => (v === 'all' || v == null ? '—' : v);

  return (
    <div style={{ color: COLORS.text, fontFamily: "'Work Sans', sans-serif" }}>
      {/* Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '14px',
      }}>
        <div>
          <label style={labelStyle}>Component</label>
          <select
            value={component}
            onChange={(e) => {
              setComponent(e.target.value);
              setTxZone(ALL);
              setVoltage(ALL);
              setSeason(ALL);
              setTouPeriod(ALL);
            }}
            style={selectStyle}
          >
            {components.map((c) => (
              <option key={c} value={c}>{COMPONENT_LABELS[c] || c}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Tx zone</label>
          <select value={txZone} onChange={(e) => setTxZone(e.target.value)} style={selectStyle}>
            <option value={ALL}>All</option>
            {available.txZone.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Voltage</label>
          <select value={voltage} onChange={(e) => setVoltage(e.target.value)} style={selectStyle}>
            <option value={ALL}>All</option>
            {available.voltage.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Season</label>
          <select value={season} onChange={(e) => setSeason(e.target.value)} style={selectStyle}>
            <option value={ALL}>All</option>
            {available.season.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label style={labelStyle}>TOU period</label>
          <select value={touPeriod} onChange={(e) => setTouPeriod(e.target.value)} style={selectStyle}>
            <option value={ALL}>All</option>
            {available.touPeriod.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end', fontSize: '12px', color: COLORS.textDim }}>
          {filtered.length} row{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px',
          background: COLORS.danger,
          color: COLORS.dangerText,
          borderRadius: '4px',
          marginBottom: '10px',
          fontSize: '12px',
        }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Tx zone</th>
              <th style={thStyle}>Voltage</th>
              <th style={thStyle}>Season</th>
              <th style={thStyle}>TOU</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
              <th style={thStyle}>Unit</th>
              <th style={thStyle}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: COLORS.textDim, fontStyle: 'italic' }}>
                  No charges match filters
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const isEditing = editingId === c.id;
              return (
                <tr key={c.id}>
                  <td style={tdStyle}>{renderDim(c.tx_zone)}</td>
                  <td style={tdStyle}>{renderDim(c.voltage_category)}</td>
                  <td style={tdStyle}>{renderDim(c.season)}</td>
                  <td style={tdStyle}>{renderDim(c.tou_period)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <input
                          type="number"
                          step="0.0001"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(c.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          style={{
                            background: COLORS.panelAlt,
                            color: COLORS.text,
                            border: `1px solid ${COLORS.accent}`,
                            borderRadius: '3px',
                            padding: '4px 6px',
                            fontSize: '12px',
                            width: '100px',
                            textAlign: 'right',
                            fontFamily: 'inherit',
                          }}
                        />
                        <button
                          onClick={() => commitEdit(c.id)}
                          disabled={saving}
                          style={{
                            background: COLORS.accent,
                            color: '#0f172a',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          style={{
                            background: 'transparent',
                            color: COLORS.textDim,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '3px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => beginEdit(c)}
                        style={{
                          background: 'transparent',
                          color: COLORS.text,
                          border: `1px solid transparent`,
                          borderRadius: '3px',
                          padding: '2px 6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${COLORS.border}`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.border = `1px solid transparent`; }}
                      >
                        {c.value}
                      </button>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: COLORS.textDim }}>{c.unit}</td>
                  <td style={{ ...tdStyle, color: COLORS.textDim }}>{c.notes || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TariffChargesTable;
